import React, { useState } from 'react';
import { Box, IconButton, Collapse } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import PlaceIcon from '@mui/icons-material/Place';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import TagIcon from '@mui/icons-material/LocalOffer';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import dayjs from 'dayjs';
import {
    AboutContainer,
    AboutHeader,
    AboutTitle,
    AboutDescription,
    ReadMoreButton,
    StatsGrid,
    StatCard,
    TagsContainer,
    CategoryTag,
    SocialLinks,
    CollapsedAbout,
} from './styled';

const AboutCommunityView = ({
    community,
    members = [],
    venues = [],
    upcomingEvents = [],
    isMember = false,
}) => {
    const [expanded, setExpanded] = useState(!isMember); // Auto-expand for non-members
    const [showFullDescription, setShowFullDescription] = useState(false);

    // Calculate stats
    const activeMembersCount = members.filter(m => m.status === 'AC').length;
    const activeVenuesCount = venues.filter(v => v.status === 'AC').length;
    const upcomingEventsCount = upcomingEvents.length;
    const foundedYear = community?.create_date 
        ? dayjs(community.create_date).year() 
        : null;

    // Get categories/tags from community
    const categories = community?.categories || community?.tags || [];
    
    // Default tags if none provided
    const displayTags = categories.length > 0 
        ? categories 
        : ['Coworking', 'Networking', 'Professional'];

    // Social links
    const socialLinks = {
        website: community?.website,
        linkedin: community?.linkedin_url,
        twitter: community?.twitter_url,
        instagram: community?.instagram_url,
    };

    const hasSocialLinks = Object.values(socialLinks).some(link => link);

    // For members, show a collapsed version
    if (isMember) {
        return (
            <CollapsedAbout onClick={() => setExpanded(!expanded)}>
                <Box className="collapsed-left">
                    <InfoIcon sx={{ color: '#F1C40F' }} />
                    <Box>
                        <span className="collapsed-title">About {community?.name}</span>
                        {!expanded && (
                            <span className="collapsed-preview">
                                {community?.description?.substring(0, 60)}...
                            </span>
                        )}
                    </Box>
                </Box>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                
                <Collapse in={expanded} sx={{ width: '100%', mt: 2 }}>
                    <AboutDescription expanded={showFullDescription}>
                        {community?.description || 'Welcome to our community! A place for like-minded professionals to connect, collaborate, and grow together.'}
                    </AboutDescription>
                </Collapse>
            </CollapsedAbout>
        );
    }

    // Full view for non-members
    return (
        <AboutContainer>
            <AboutHeader>
                <Box className="header-icon">
                    <InfoIcon />
                </Box>
                <AboutTitle>
                    About {community?.name || 'This Community'}
                </AboutTitle>
            </AboutHeader>

            <AboutDescription expanded={showFullDescription}>
                {community?.description || 
                    'Welcome to our community! We bring together like-minded professionals who are passionate about their work. Our members enjoy exclusive access to partner coworking spaces, networking events, and resources designed to help you thrive in your career.'}
            </AboutDescription>

            {community?.description && community.description.length > 200 && (
                <ReadMoreButton onClick={() => setShowFullDescription(!showFullDescription)}>
                    {showFullDescription ? 'Show less' : 'Read more'}
                    <KeyboardArrowRightIcon sx={{ 
                        transform: showFullDescription ? 'rotate(-90deg)' : 'rotate(90deg)' 
                    }} />
                </ReadMoreButton>
            )}

            <StatsGrid>
                <StatCard>
                    <div className="stat-value">{activeMembersCount}</div>
                    <div className="stat-label">Members</div>
                </StatCard>
                <StatCard>
                    <div className="stat-value">{activeVenuesCount}</div>
                    <div className="stat-label">Venues</div>
                </StatCard>
                <StatCard>
                    <div className="stat-value">{upcomingEventsCount}</div>
                    <div className="stat-label">Events</div>
                </StatCard>
                {foundedYear && (
                    <StatCard>
                        <div className="stat-value">{foundedYear}</div>
                        <div className="stat-label">Founded</div>
                    </StatCard>
                )}
                {!foundedYear && (
                    <StatCard>
                        <div className="stat-value">∞</div>
                        <div className="stat-label">Growing</div>
                    </StatCard>
                )}
            </StatsGrid>

            <TagsContainer>
                {displayTags.map((tag, index) => (
                    <CategoryTag
                        key={index}
                        label={typeof tag === 'string' ? tag : tag.name}
                        icon={<TagIcon />}
                        size="small"
                    />
                ))}
            </TagsContainer>

            {hasSocialLinks && (
                <SocialLinks>
                    {socialLinks.website && (
                        <a 
                            href={socialLinks.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="social-link"
                        >
                            <LanguageIcon />
                        </a>
                    )}
                    {socialLinks.linkedin && (
                        <a 
                            href={socialLinks.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="social-link"
                        >
                            <LinkedInIcon />
                        </a>
                    )}
                    {socialLinks.twitter && (
                        <a 
                            href={socialLinks.twitter} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="social-link"
                        >
                            <TwitterIcon />
                        </a>
                    )}
                    {socialLinks.instagram && (
                        <a 
                            href={socialLinks.instagram} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="social-link"
                        >
                            <InstagramIcon />
                        </a>
                    )}
                </SocialLinks>
            )}
        </AboutContainer>
    );
};

export default AboutCommunityView;
